/**
 * InsightPro™ — API Client  v5.0
 *
 * v5.0: Replaced getEmbedToken (Azure Embedded capacity model) with pbiQuery.
 *   Each client has a dedicated @insightpro.co.za Microsoft account with a
 *   Power BI Pro licence. pbiQuery calls the Cloud Function which authenticates
 *   via ROPC and returns clean JSON rows from the client's Power BI dataset.
 *   No iframe embed. No Azure Embedded capacity. No CORS issues.
 */
import axios from 'axios';
import { getIdToken } from './firebase';

const BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL;

// Authenticated axios instance — attaches Firebase ID token to every request
const api = axios.create();
api.interceptors.request.use(async (config) => {
  const token = await getIdToken();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// POST to dataApi router
function post(endpoint, body = {}) {
  return api.post(`${BASE}/dataApi/${endpoint}`, body).then(r => r.data);
}

// ── Dashboard data ────────────────────────────────────────────
export const fetchKPIs              = (period = 'MTD')  => post('getKPIs', { period });
export const fetchRevenueTrend      = (months = 6)      => post('getRevenueTrend', { months });
export const fetchBranchPerformance = (period = 'MTD')  => post('getBranchPerformance', { period });
export const fetchMarginByCategory  = (period = 'MTD')  => post('getMarginByCategory', { period });
export const fetchStockSummary      = ()                => post('getStockSummary');
export const fetchDebtorsSummary    = ()                => post('getDebtorsSummary');
export const fetchTopDebtors        = (limit = 10)      => post('getTopDebtors', { limit });
export const fetchExceptions        = ()                => post('getExceptions');
export const resolveException       = (exceptionKey)    => post('resolveException', { exceptionKey });

// ── Multi-Entity Finance (Pedros) ─────────────────────────────
export const fetchFinancePeriods   = ()                               => post('getFinancePeriods');
export const fetchFinanceBrands    = ()                               => post('getFinanceBrands');
// filters: { country, province, entityType } — any subset narrows the result.
export const fetchGroupPL          = (period, brand = 'all', filters = {}) =>
  post('getGroupPL', { period, brand, ...filters });
// opts: { groupBy, country, province, entityType, comparePeriod, trendMonths }
//   groupBy ∈ {'country','province','brand','entity_type','entity'} (default 'entity')
//   comparePeriod: 'priorMonth' to include *_prev / *_delta / *_deltaPct fields
//   trendMonths: integer N to include salesTrend: [{periodKey, sales}, ...] last N months (entity-level only)
export const fetchEntityPLSummary  = (period, brand = 'all', opts = {}) =>
  post('getEntityPLSummary', {
    period, brand,
    groupBy:       opts.groupBy       || 'entity',
    country:       opts.country       || null,
    province:      opts.province      || null,
    entityType:    opts.entityType    || null,
    comparePeriod: opts.comparePeriod || null,
    trendMonths:   opts.trendMonths   || 0,
  });
export const fetchEntityPLDetail   = (period, entityKey)             => post('getEntityPLDetail',  { period, entityKey });
export const fetchBalanceSheet     = (period, brand = 'all', filters = {}) =>
  post('getBalanceSheet', { period, brand, ...filters });
// drill: { reportCategory, glAccountKey } in addition to the same filter set
export const fetchBSEntities       = (period, brand = 'all', opts = {}) =>
  post('getBalanceSheetEntities', { period, brand, ...opts });

// Expense Analysis (Multi-Entity Finance)
// opts: { country, province, entityType, includeCoS } — includeCoS=true adds Cost of Sales into all totals
export const fetchExpenseAnalysis  = (period, brand = 'all', opts = {}) =>
  post('getExpenseAnalysis', { period, brand, ...opts });
// opts: { country, province, entityType, months, includeCoS } — months default 12
export const fetchExpenseTrend     = (period, brand = 'all', opts = {}) =>
  post('getExpenseTrend',    { period, brand, ...opts });
// opts: { country, province, entityType, includeCoS, reportCategory, glAccountKey }
export const fetchExpenseEntities  = (period, brand = 'all', opts = {}) =>
  post('getExpenseEntities', { period, brand, ...opts });

// Cash Flow (Multi-Entity Finance) — indirect method
// opts: { country, province, entityType }
export const fetchCashFlow         = (period, brand = 'all', opts = {}) =>
  post('getCashFlow',      { period, brand, ...opts });
// opts: { country, province, entityType, months }  — months default 12
export const fetchCashFlowTrend    = (period, brand = 'all', opts = {}) =>
  post('getCashFlowTrend', { period, brand, ...opts });

// ── Advisory ──────────────────────────────────────────────────
export const fetchAdvisoryNotes  = ()                               => post('getAdvisoryNotes');
export const fetchActionItems    = ()                               => post('getActionItems');
export const updateActionStatus  = (actionKey, status, notes = '') =>
  post('updateActionStatus', { actionKey, status, notes });

// ── Power BI Data Query ───────────────────────────────────────
// Calls the pbiQuery Cloud Function which authenticates against the
// client's Power BI Pro workspace and returns clean JSON rows.
// Pass either:
//   { table: 'KPIs', period: 'MTD' }          — shorthand (function builds DAX)
//   { dax: 'EVALUATE ROW(...)' }               — raw DAX for custom queries
export const pbiQuery = async (params = {}) => {
  const token = await getIdToken();

  // companyId resolution — priority order:
  // 1. Explicitly passed in params (e.g. from StockOptimisation, Dashboard, SalesPage)
  // 2. localStorage keyed by UID (persists across PWA restarts)
  let companyId = params.companyId || null;

  if (!companyId) {
    try {
      const { getAuth } = await import('firebase/auth');
      const uid = getAuth().currentUser?.uid;
      if (uid) {
        const stored = localStorage.getItem(`insightpro_company_${uid}`);
        if (stored) companyId = JSON.parse(stored)?.id || JSON.parse(stored)?.companyId;
      }
    } catch {}
  }

  const r = await axios.post(`${BASE}/pbiQuery`, { ...params, companyId }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return r.data;
};

// ── Management Report PDF (Phase 2) ─────────────────────────────
// Invokes the generateReport Cloud Function which spins up puppeteer,
// renders /report-render for each brand + Consolidated, uploads each PDF
// to GCS, and returns signed URLs (7-day TTL).
//
// Returns: { results: [{ brand, signedUrl, sizeBytes, ms }], errors: [...] }
export const generateReport = async ({ companyId, period, brands }) => {
  const token = await getIdToken();
  const r = await axios.post(`${BASE}/generateReport`,
    { companyId, period, brands },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return r.data;
};

// ── Management Report AI commentary (Phase 3) ───────────────────
// Returns { commentary: { execSummary, pl, balanceSheet, cashFlow, expense,
// entities, geography }, cached: bool, meta: { model, inputTokens, ... } }.
// Cached server-side in Firestore by (clientSlug, period, brand) — pass
// regenerate=true to bust the cache and force a fresh Claude call.
export const fetchReportCommentary = (period, brand = 'all', opts = {}) =>
  post('getReportCommentary', {
    period,
    brand,
    companyName: opts.companyName || null,
    regenerate: Boolean(opts.regenerate),
  });
